import React, { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { toastr } from 'react-redux-toastr';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Grid, Typography, TextField, Switch, FormControlLabel, IconButton, MenuItem } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';
import { useFormik } from 'formik';
import * as yup from 'yup';
import InputMask from 'react-input-mask';
import { useLocation, useHistory } from 'react-router-dom';

import TituloPagina from '../../components/layout/TituloPagina';
import FileList from '../../components/templates/fileList/FileList';
import If from '../../components/condicional/if';

import {
    carregarUploadedFiles,
    addCampanha,
    getCampanha,
    getVeiculoPublicidadeAtivos,
    atualizarCampanha,
} from '../../store/actions/publicidade';

import {
    Container,
    Content,
    DropContainer,
    UploadMessage
} from './styles';

const targetTypeOptions = [
    { value: 'ALL_USERS', label: 'Todos os usuários' },
    { value: 'SPECIFIC_USER', label: 'Usuário específico' },
    { value: 'ROLE', label: 'Perfil de usuário' },
];

const actionTypeOptions = [
    { value: 'OPEN_SCREEN', label: 'Abrir tela' },
    { value: 'EXTERNAL_URL', label: 'Abrir URL externa' },
    { value: 'READ_ONLY', label: 'Somente leitura' },
];

const MAX_SIZE = 1500000; // 1,5mb
const MAX_FILES = 1;

const CadastrarCampanhaPublicitaria = props => {
    const location = useLocation();
    const history = useHistory();
    const {
        uploadedFiles,
        carregarUploadedFiles,
        addCampanha,
        atualizarCampanha,
        getCampanha,
        getVeiculoPublicidadeAtivos,
        campanha,
        veiculosPublicidadeAtivos,
    } = props;

    const [imagemAnexada, setImagemAnexada] = useState(false);
    const [disableFields, setDisableFields] = useState(false);
    const [imagemBannerExistente, setImagemBannerExistente] = useState(null);
    const id = Number(location?.state?.campanhaId);
    const isEditRoute = location.pathname === '/campanhas-publicitarias-no-app/editar-campanha';
    const isVisualizarRoute = location.pathname === '/campanhas-publicitarias-no-app/visualizar-campanha';

    useEffect(() => {

        if (isEditRoute && !id) {
            history.replace('/campanhas');
            return;
        }

        if (isVisualizarRoute && !id) {
            history.replace('/campanhas');
            return;
        }

        if (isVisualizarRoute) {
            setDisableFields(true);
        }

        if (id > 0) {
            getCampanha(id);
        }
    }, [getCampanha, history, location]);

    useEffect(() => {
        getVeiculoPublicidadeAtivos();
    }, [getVeiculoPublicidadeAtivos]);

    const handleUpload = (files) => {
        const currentCount = uploadedFiles.length;

        if (currentCount >= MAX_FILES) {
            toastr.error('Erro', 'Limite de 1 imagem atingido.');
            return;
        }

        const filesToProcess = files.slice(0, MAX_FILES - currentCount);

        if (files.length > (MAX_FILES - currentCount)) {
            toastr.warning('Aviso', 'Alguns arquivos foram ignorados (limite de 1).');
        }

        const newUploads = filesToProcess.map((file, index) => ({
            file,
            idTemp: uniqueId('upload_'), // ID único por ARQUIVO (essencial para progresso)
            readableSize: filesize(file.size),
            preview: URL.createObjectURL(file),
            progress: 0,
            uploaded: false,
            error: false,
            url: null
        }));

        // Atualiza a lista no Redux
        carregarUploadedFiles([...uploadedFiles, ...newUploads]);

        setImagemAnexada(true);
    };

    const handleRemoverImagemExistente = () => {
        setImagemBannerExistente(null);
        setImagemAnexada(false);
        formik.setFieldValue('imagemBanner', '');
    };

    const parseDateString = (value, originalValue) => {
        if (!originalValue || originalValue.includes('_')) return null;

        const [day, month, year] = originalValue.split('/').map(Number);
        const date = new Date(year, month - 1, day);

        // Validação de consistência:
        // Se o mês ou dia mudaram, significa que o JS "corrigiu" uma data inválida.
        if (date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day) {
            return date;
        }

        // Retorna uma data inválida para o Yup disparar o typeError
        return new Date('');
    };

    const validationSchema = yup.object({
        titulo: yup
            .string()
            .required('Informe um título ou nome para a campanha.')
            .min(5, 'O nome deve possuir entre 5 e 100 caracteres.')
            .max(100, 'O nome deve possuir entre 5 e 100 caracteres.'),
        conteudo: yup.string()
            .required('Escreva um texto resumido para a campanha que será exibido no modal.')
            .max(255, 'O texto deve possuir até 255 caracteres.'),
        dataInicioVeiculacao: yup
            .date()
            .transform(parseDateString)
            .typeError('Data inválida (use DD/MM/AAAA).')
            .required('Informe a data de início.'),
        dataFimVeiculacao: yup
            .date()
            .transform(parseDateString)
            .typeError('Data inválida (use DD/MM/AAAA).')
            .required('Informe a data de fim.')
            .min(
                yup.ref('dataInicioVeiculacao'),
                'A data de fim deve ser posterior à data de início.'
            ),
        veiculos: yup
            .array()
            .min(1, 'Selecione pelo menos um veículo de publicidade.')
            .required('Selecione pelo menos um veículo de publicidade.'),
    });

    const formik = useFormik({
        initialValues: {
            id: campanha?.id || 0,
            titulo: campanha?.titulo || '',
            conteudo: campanha?.conteudo || '',
            conteudoExpandido: campanha?.conteudoExpandido || '',
            targetType: campanha?.targetType || '',
            targetId: campanha?.targetId || '',
            actionType: campanha?.actionType || '',
            actionPayload: campanha?.actionPayload || '',
            dataInicioVeiculacao: campanha?.dataInicioVeiculacao || '',
            dataFimVeiculacao: campanha?.dataFimVeiculacao || '',
            imagemBanner: campanha?.imagemBanner || '',
            ativo: campanha?.ativo || false,
            veiculos: campanha?.veiculos || [],
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {

            if (!imagemAnexada) {
                toastr.warning('Alerta', 'Anexe a imagem da campanha para continuar.');
                return;
            }

            const file = uploadedFiles[0];
            const newValues = { ...values, ...file }

            if (isEditRoute) {
                atualizarCampanha(newValues);
            } else {
                addCampanha(newValues);
            }

            setDisableFields(true);

            toastr.success('Sucesso', 'Campanha salva com sucesso!');
        },
    });

    useEffect(() => {
        if (campanha?.imagemBanner) {
            setImagemBannerExistente(campanha.imagemBanner);
            setImagemAnexada(true);
        }
    }, [campanha]);

    const handleTargetTypeChange = (event) => {
        formik.handleChange(event);

        if (event.target.value === 'ALL_USERS') {
            formik.setFieldValue('targetId', '');
        }
    };

    return (
        <>
            <TituloPagina titulo='Cadastro de Campanha Publicitária' />

            <Grid container item xs={12} style={{ padding: '15px 0' }}>
                <Typography variant="body1">
                    A imagem deve ser PNG ou JPG (Máx. 1,5Mb). Para garantir nitidez total em quase todos os dispositivos, recomendamos trabalhar com as medidas 1080x400 pixels.
                </Typography>
            </Grid>

            <Container>
                <Content>
                    <If test={Boolean(imagemBannerExistente)}>
                        <Grid container item xs={12} style={{ paddingBottom: '15px' }}>
                            <Grid item style={{ position: 'relative', width: '100%', maxWidth: 400 }}>
                                <img
                                    src={imagemBannerExistente}
                                    alt='Imagem da campanha'
                                    style={{ width: '100%', borderRadius: 4, display: 'block' }}
                                />
                                <If test={!disableFields}>
                                    <IconButton
                                        onClick={handleRemoverImagemExistente}
                                        style={{ position: 'absolute', top: 4, right: 4, backgroundColor: '#fff' }}
                                        title='Excluir imagem'
                                    >
                                        <DeleteIcon color='error' />
                                    </IconButton>
                                </If>
                            </Grid>
                        </Grid>
                    </If>

                    <If test={!imagemBannerExistente}>
                        <Dropzone
                            accept={{ 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }}
                            onDrop={handleUpload}
                            maxSize={MAX_SIZE}
                        >
                            {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                                <DropContainer
                                    {...getRootProps()}
                                    $isDragActive={isDragActive}
                                    $isDragReject={isDragReject}
                                >
                                    <input {...getInputProps()} />
                                    {!isDragActive && <UploadMessage>Clique ou arraste imagens aqui.</UploadMessage>}
                                    {isDragActive && !isDragReject && <UploadMessage type="success">Solte para enviar!</UploadMessage>}
                                    {isDragReject && <UploadMessage type="error">Arquivo não suportado ou muito grande.</UploadMessage>}
                                </DropContainer>
                            )}
                        </Dropzone>

                        {uploadedFiles.length > 0 && (
                            <FileList
                                files={uploadedFiles}
                            />
                        )}
                    </If>

                    <form onSubmit={formik.handleSubmit} noValidate>
                        <div style={{ flexGrow: 1, paddingTop: '15px' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="titulo"
                                        name="titulo"
                                        label="Título da Campanha"
                                        variant="filled"
                                        disabled={disableFields}
                                        value={formik.values.titulo}
                                        onChange={formik.handleChange}
                                        error={formik.touched.titulo && Boolean(formik.errors.titulo)}
                                        helperText={formik.touched.titulo && formik.errors.titulo} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="conteudo"
                                        name="conteudo"
                                        label="Texto Resumido da Campanha"
                                        variant="filled"
                                        disabled={disableFields}
                                        value={formik.values.conteudo}
                                        onChange={formik.handleChange}
                                        error={formik.touched.conteudo && Boolean(formik.errors.conteudo)}
                                        helperText={formik.touched.conteudo && formik.errors.conteudo} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="conteudoExpandido"
                                        name="conteudoExpandido"
                                        label="Texto Expandido da Campanha"
                                        variant="filled"
                                        multiline
                                        minRows={3}
                                        disabled={disableFields}
                                        value={formik.values.conteudoExpandido}
                                        onChange={formik.handleChange}
                                        error={formik.touched.conteudoExpandido && Boolean(formik.errors.conteudoExpandido)}
                                        helperText={formik.touched.conteudoExpandido && formik.errors.conteudoExpandido}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        id='targetType'
                                        name='targetType'
                                        label='Público-alvo'
                                        variant='filled'
                                        disabled={disableFields}
                                        value={formik.values.targetType}
                                        onChange={handleTargetTypeChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.targetType && Boolean(formik.errors.targetType)}
                                        helperText={formik.touched.targetType && formik.errors.targetType}
                                    >
                                        <MenuItem value=''>Selecione o público</MenuItem>
                                        {targetTypeOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        id='targetId'
                                        name='targetId'
                                        label='Identificador do público-alvo'
                                        variant='filled'
                                        disabled={disableFields || formik.values.targetType === 'ALL_USERS'}
                                        value={formik.values.targetId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.targetId && Boolean(formik.errors.targetId)}
                                        helperText={formik.touched.targetId && formik.errors.targetId
                                            ? formik.errors.targetId
                                            : 'Informe o ID do usuário ou o nome do perfil selecionado.'}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant='subtitle1' component='p'>Ação ao abrir a notificação</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        id='actionType'
                                        name='actionType'
                                        label='Tipo de ação'
                                        variant='filled'
                                        disabled={disableFields}
                                        value={formik.values.actionType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.actionType && Boolean(formik.errors.actionType)}
                                        helperText={formik.touched.actionType && formik.errors.actionType}
                                    >
                                        <MenuItem value=''>Nenhuma ação</MenuItem>
                                        {actionTypeOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id='actionPayload'
                                        name='actionPayload'
                                        label='Dados da ação (JSON)'
                                        variant='filled'
                                        multiline
                                        rows={5}
                                        disabled={disableFields}
                                        value={formik.values.actionPayload}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.actionPayload && Boolean(formik.errors.actionPayload)}
                                        helperText={formik.touched.actionPayload && formik.errors.actionPayload
                                            ? formik.errors.actionPayload
                                            : 'Ex.: {"screen":"OrderDetails","params":{"orderId":"123"}}'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputMask
                                        mask={'99/99/9999'}
                                        maskChar=' '
                                        disabled={disableFields}
                                        value={formik.values.dataInicioVeiculacao}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur} // <--- Adicione isso para a validação disparar ao sair do campo
                                    >
                                        {(inputProps) => ( // <--- Passe as props do mask para o TextField
                                            <TextField
                                                {...inputProps} // <--- Importante para o mask funcionar 100%
                                                fullWidth
                                                id='dataInicioVeiculacao'
                                                name='dataInicioVeiculacao'
                                                label='Data de Início da Veiculação'
                                                variant='filled'
                                                error={formik.touched.dataInicioVeiculacao && Boolean(formik.errors.dataInicioVeiculacao)}
                                                helperText={formik.touched.dataInicioVeiculacao && formik.errors.dataInicioVeiculacao}
                                            />
                                        )}
                                    </InputMask>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputMask
                                        mask={'99/99/9999'}
                                        maskChar=' '
                                        disabled={disableFields}
                                        value={formik.values.dataFimVeiculacao}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        {(inputProps) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                id='dataFimVeiculacao'
                                                name='dataFimVeiculacao'
                                                label='Data de Fim da Veiculação'
                                                variant='filled'
                                                error={formik.touched.dataFimVeiculacao && Boolean(formik.errors.dataFimVeiculacao)}
                                                helperText={formik.touched.dataFimVeiculacao && formik.errors.dataFimVeiculacao}
                                            />)}
                                    </InputMask>
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        id="veiculos"
                                        disabled={disableFields}
                                        options={veiculosPublicidadeAtivos || []}
                                        getOptionLabel={(option) => option.nome || ''}
                                        getOptionSelected={(option, value) => option.id === value.id}
                                        // formik.values.veiculos guarda apenas os IDs; aqui resolvemos os objetos correspondentes para exibição
                                        value={(veiculosPublicidadeAtivos || []).filter(option => formik.values.veiculos.includes(option.id))}
                                        onChange={(event, newValue) => {
                                            formik.setFieldValue('veiculos', newValue.map(option => option.id));
                                        }}
                                        onBlur={() => formik.setFieldTouched('veiculos', true)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                variant="filled"
                                                label="Veículos de Publicidade"
                                                placeholder="Selecione um ou mais locais de veiculação"
                                                error={formik.touched.veiculos && Boolean(formik.errors.veiculos)}
                                                helperText={formik.touched.veiculos && formik.errors.veiculos}
                                            />
                                        )}
                                        noOptionsText="Nenhum veículo disponível"
                                        clearText="Limpar"
                                        openText="Abrir"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch
                                            checked={formik.values.ativo}
                                            onChange={formik.handleChange}
                                            name="ativo"
                                            disabled={disableFields} />}
                                        label="Campanha Ativa"
                                    />
                                </Grid>

                            </Grid>
                            <Grid container spacing={1} pt={4} style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                                <If test={!disableFields}>
                                    <Grid item style={{ paddingRight: '15px' }}>
                                        <Button type='submit' color='primary' variant='contained' disabled={disableFields}>Salvar</Button>
                                    </Grid>
                                    <Grid item style={{ paddingRight: '15px' }}>
                                        <Button variant='contained' href={`/campanhas-publicitarias-no-app`}>Cancelar</Button>
                                    </Grid>
                                </If>
                                <If test={disableFields}>
                                    <Grid item style={{ paddingRight: '15px' }}>
                                        <Button variant='contained' href={`/campanhas-publicitarias-no-app`}>Voltar</Button>
                                    </Grid>
                                </If>
                            </Grid>
                        </div>
                    </form>
                </Content>
            </Container>
        </>
    );
};

const mapStateToProps = state => ({
    uploadedFiles: state.publicidade.uploadedFiles,
    campanha: state.publicidade.campanha,
    veiculosPublicidadeAtivos: state.publicidade.veiculosPublicidadeAtivos
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            carregarUploadedFiles,
            addCampanha,
            getCampanha,
            getVeiculoPublicidadeAtivos,
            atualizarCampanha
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(CadastrarCampanhaPublicitaria);